import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import {
  Lock as LockIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { TrialIndicator } from './TrialIndicator';

const FEATURE_REQUIREMENTS = {
  appointments: { minPlan: 'basic', limit: 50 },
  patients: { minPlan: 'basic', limit: 100 },
  consultations: { minPlan: 'professional', limit: null },
  prescriptions: { minPlan: 'professional', limit: null },
  analytics: { minPlan: 'professional', limit: null },
  billing: { minPlan: 'basic', limit: null },
  settings: { minPlan: 'basic', limit: null }, // Settings always accessible
  profile: { minPlan: 'basic', limit: null }, // Profile always accessible
};

const FeatureBlocker = ({ feature, reason, action, isLoading }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleAction = () => {
    if (action === 'upgrade') {
      navigate('/settings', { state: { activeTab: 'subscription' } });
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
        textAlign: 'center',
        bgcolor: 'background.default',
        border: '1px dashed',
        borderColor: 'divider'
      }}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {feature} Not Available
          </Typography>
          <Typography color="text.secondary" paragraph>
            {reason}
          </Typography>
          {action === 'upgrade' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAction}
              startIcon={<CheckCircleIcon />}
            >
              Upgrade Now
            </Button>
          )}
        </>
      )}
    </Card>
  );
};

const UsageWarning = ({ feature, current, limit, onUpgrade }) => {
  const percentage = (current / limit) * 100;
  const isNearLimit = percentage >= 80;
  const severity = percentage >= 90 ? 'error' : 'warning';

  return (
    <Alert
      severity={severity}
      action={
        <Button color="inherit" size="small" onClick={onUpgrade}>
          Upgrade Plan
        </Button>
      }
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {isNearLimit ? `${feature} Usage Critical!` : `${feature} Usage Warning`}
        </Typography>
        <Typography variant="body2">
          You've used {current} of {limit} {feature.toLowerCase()} ({Math.round(percentage)}%).
          {isNearLimit && ' Consider upgrading to avoid service interruption.'}
        </Typography>
      </Box>
    </Alert>
  );
};

export const SubscriptionAwareDoctorDashboard = ({ children }) => {
  const navigate = useNavigate();
  const {
    loading: isLoading,
    isAuthenticated,
    hasActiveSubscription,
    isOnTrial,
    getPlanType,
    getCurrentUsage,
    refresh
  } = useSubscription();

  const getCurrentFeature = () => {
    const path = window.location.pathname.toLowerCase();
    for (const [feature, requirements] of Object.entries(FEATURE_REQUIREMENTS)) {
      if (path.includes(feature.toLowerCase())) {
        return { name: feature, ...requirements };
      }
    }
    return null;
  };

  const checkFeatureAccess = (feature) => {
    if (!feature) return { hasAccess: true }; // Unknown features are accessible
    if (feature.name === 'settings' || feature.name === 'profile') return { hasAccess: true }; // Always accessible

    const currentPlan = getPlanType();
    const planAccess = {
      basic: ['basic'],
      professional: ['basic', 'professional'],
      enterprise: ['basic', 'professional', 'enterprise']
    };

    // During trial, grant access to all features
    if (isOnTrial()) {
      return { hasAccess: true, isTrial: true };
    }

    // Check plan-based access
    const allowedPlans = planAccess[currentPlan] || [];
    if (!allowedPlans.includes(feature.minPlan)) {
      return {
        hasAccess: false,
        reason: `This feature requires a ${feature.minPlan} plan or higher`,
        action: 'upgrade'
      };
    }

    // Check usage limits
    if (feature.limit) {
      const usage = getCurrentUsage(feature.name);
      if (usage >= feature.limit) {
        return {
          hasAccess: false,
          reason: `You've reached the ${feature.name} limit for your current plan`,
          action: 'upgrade'
        };
      }
      if (usage >= feature.limit * 0.8) {
        return {
          hasAccess: true,
          warning: {
            current: usage,
            limit: feature.limit
          }
        };
      }
    }

    return { hasAccess: true };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Card sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Authentication Required
          </Typography>
          <Typography paragraph>
            Please log in to access the doctor dashboard.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
          >
            Log In
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentFeature = getCurrentFeature();
  const accessCheck = checkFeatureAccess(currentFeature);

  // Show trial indicator if on trial
  const showTrialIndicator = isOnTrial() && !hasActiveSubscription();

  // If no access, show blocker
  if (!accessCheck.hasAccess) {
    return (
      <>
        {showTrialIndicator && <TrialIndicator variant="banner" />}
        <FeatureBlocker
          feature={currentFeature?.name}
          reason={accessCheck.reason}
          action={accessCheck.action}
          isLoading={isLoading}
        />
      </>
    );
  }

  // If has access but with warning, show both warning and content
  return (
    <>
      {showTrialIndicator && <TrialIndicator variant="banner" />}
      
      {accessCheck.warning && (
        <UsageWarning
          feature={currentFeature.name}
          current={accessCheck.warning.current}
          limit={accessCheck.warning.limit}
          onUpgrade={() => navigate('/settings', { state: { activeTab: 'subscription' } })}
        />
      )}

      {/* If on trial, show compact trial indicator on feature pages */}
      {showTrialIndicator && currentFeature && (
        <Box mb={2}>
          <TrialIndicator variant="compact" />
        </Box>
      )}

      {children}
    </>
  );
}; 